import {IncomingMessage} from 'http'
import {captureException} from '@sentry/nextjs'
import axios, {AxiosInstance, AxiosRequestConfig} from 'axios'
import {adapterEnhancer} from '@big-data/react-forge'
// @ts-ignore
import xhr from '../node_modules/axios/lib/adapters/xhr'
import {Toast} from 'gotu-ui'
import {NETWORK_ERROR_TOAST, SUCCESS_CODE} from '@/constants'
import {errorLog} from '@/utils/log'
import {IS_BUILD, IS_PROD, IS_SERVER, IS_TEST} from '@/constants/env'
import getUaParam, {getPublicParams} from '@/utils/getUaParam'
import canRefresh from '@/components/CustomError/canRefresh'
import {APIError} from '@/constants/error'
import {TIMEOUT} from '../constants'
import {niceTry, niceTryAsync} from '../utils/index'
import CircularJSON from 'circular-json'

const logger = typeof window === 'undefined' ? require('../serverModule/logger') : {}

const baseURL = IS_SERVER ? process.env.NEXT_PUBLIC_API_BASE_URL_INTERNAL : process.env.NEXT_PUBLIC_API_BASE_URL
const baseURLMock = process.env.NEXT_PUBLIC_API_BASE_URL_MOCK
const genErrorHandler = (renderId: string, instance: AxiosInstance) => (err: any) => {
    logger.requestError?.('请求失败', {renderId, err, instance})
    // 不是error对象转为error对象, 不然sentry报的有问题
    // https://zhuanlan.zhihu.com/p/75577689
    captureException(new Error('请求失败'), (scope) => scope.setExtras({renderId, extraInfo: err}))
    errorLog(err)
    return Promise.reject(err)
}

export const getHttp = (renderId: string, base:string | undefined = baseURL) => {
    // @ts-ignore
    const http = axios.create({baseURL: base, adapter: adapterEnhancer({adapter: xhr})})
    http.interceptors.request.use(
        (config) => {
            return config
        },
        genErrorHandler(renderId, http)
    )
    http.interceptors.response.use(
        (res) => res,
        genErrorHandler(renderId, http)
    )
    return http
}

const axiosToCurl = (config: AxiosRequestConfig) => {
    const {
        method,
        url,
        headers,
        data,
        params,
    } = config
    const paramStr = params ? `?${Object.entries(params).map(([key, value]) => `${key}=${value}`)
        .join('&')}` : ''
    let curlCommand = `curl -X ${method} "${config.baseURL || baseURL}${url}${paramStr}"`
    if (headers) {
        for (const key in headers) {
            curlCommand += ` -H "${key}:${headers[key]}"`
        }
    }
    if (data) {
        const encodedData = JSON.stringify(data).replace(/"/g, '\\"')
        curlCommand += ` -d "${encodedData}"`
    }

    return curlCommand
}

interface IRequestProps {
    method: string;
    url: string;
    baseURL?: string;
    data?: {
        [key: string]: any;
    };
    params?: {
        [key: string]: any;
    };
    headers?: {
        [key: string]: any;
    };
    noErrToast?: boolean;
    mock?: boolean;
    ctx?: CTX;
}

const request = async <T>(props: IRequestProps, successCode = SUCCESS_CODE) => {
    const {
        method = 'get',
        url,
        baseURL,
        data = {},
        params = {},
        headers = {},
        mock,
        ctx,
    } = props
    const {req} = ctx || {}
    const {url: serverUrl} = req || {url: undefined}
    const publicParams = getPublicParams(serverUrl)
    const {traffic_env} = getUaParam(serverUrl)
    const renderId = IS_SERVER ? req?.headers.renderId : window?.__NEXT_DATA__?.props?.renderId
    const trafficEnv = (IS_SERVER ? req?.headers['traffic-env'] : window?.__NEXT_DATA__?.props?.trafficEnv) || traffic_env
    const p_logger_id = renderId
    const config: AxiosRequestConfig = {
        method,
        url,
        data,
        params: {
            renderId,
            p_logger_id,
            ...publicParams,
            ...params,
        },
        headers: {
            'Content-Type': 'application/json',
            // 后端灰度
            'traffic-env': IS_TEST ? trafficEnv : undefined,
            ...headers,
        },
        timeout: TIMEOUT,
    }
    // 添加用户sid
    if (publicParams.sid && config.headers) {
        config.headers.sid = publicParams.sid
    }
    // 添加设备id，用于图书获取线索
    if (publicParams.did && config.headers) {
        config.headers.did = publicParams.did
    }
    if (!IS_BUILD && mock) {
        config.baseURL = baseURLMock
    }
    if (baseURL !== undefined) {
        config.baseURL = baseURL
    }
    logger.requestStart?.('请求开始', {renderId, ...config})

    const http = getHttp(renderId, config.baseURL)
    const curl = axiosToCurl(config)
    if (ctx) {
        ctx.res.setHeader('curl', curl)
    }
    const timeStart = Date.now()
    const [res, error] = await niceTryAsync(http<IResponse<T>>(config))
    const timeEnd = Date.now()
    if (res?.data?.code !== successCode) {
        const {code = APIError.customError, message = error?.message || error} = res?.data || {}
        const errRes: IError = {
            code,
            message,
            msg: message,
            data: null,
            error: niceTry(() => JSON.parse(CircularJSON.stringify(res))) || null,
            success: false,
            canRefresh: canRefresh(code),
            curl,
        }
        logger.requestEnd?.('请求失败', {renderId, res: errRes})
        return errRes
    }
    logger.requestEnd?.('请求结束', {...res, renderId, elapsed: timeEnd - timeStart})
    const successRes: ISuccess<T> = {
        ...res.data,
        message: res.data.message || res.data.msg!,
        msg: res.data.message || res.data.msg!,
        success: true,
        canRefresh: canRefresh(res.data.code),
        curl,
    }
    return successRes
}

export default request
